import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../../axiosConfig';
import { addToCart } from '../../features/cart/cartSlice';
import { useGA4 } from '../../contexts/GA4Context';
import { useToast } from '../ui/Toast';
import { getStorefrontVariantAvailability } from '../../utils/storefrontVariantUtils';

/**
 * The "all products" catalog block at the bottom of the storefront
 * home page: section header, compact filter bar (search + category +
 * sort), grid/list view toggle, paginated product grid, and empty
 * state.
 *
 * Why bundled in one file: the ProductCard, the filter bar, the
 * pagination, and the empty state are all part of the same catalog —
 * splitting them across files would just trade a long section for a
 * tangle of co-dependent components. The icons live here because
 * nothing else on the home page uses them anymore.
 *
 * The parent owns all filter / sort / pagination state (search,
 * category, sort, viewMode, currentPage) because ShopByCategory and
 * the hero search input also mutate it. This component is the
 * presentational shell; it reads state, emits change handlers.
 *
 * `sectionRef` is the scroll target ShopByCategory uses when a
 * category card is clicked — the catalog needs to know its own DOM
 * root, but the parent owns the reference so it can call
 * .scrollIntoView() from outside.
 */

const CARD_RADIUS = '12px';
const CARD_SHADOW = '0 1px 3px rgba(0,0,0,0.06)';
const CARD_HOVER_SHADOW = '0 8px 25px rgba(0,0,0,0.08)';
const SECTION_MAX_WIDTH = '1200px';
const SECTION_HPAD = '1.5rem';

const SearchIcon = ({ size = 20, color = '#6b7280' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const PackageIcon = ({ size = 48, color = '#d1d5db' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16.5 9.4l-9-5.19M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

const GridIcon = ({ size = 18, color = '#6b7280' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
  </svg>
);

const ListIcon = ({ size = 18, color = '#6b7280' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

const ChevronLeftIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const HeartIcon = ({ filled = false, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? '#ec4899' : 'none'} stroke={filled ? '#ec4899' : '#6b7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const EmptySearchIcon = ({ size = 48, color = '#d1d5db' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
    <line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);

const hasProductOptions = (product) => {
  if (product?.variantMatrix?.variants?.length > 0) return true;
  if (product?.variations && !Array.isArray(product.variations) && product.variations.variants?.length > 0) return true;
  return Array.isArray(product?.variations) && product.variations.some((variation) => variation?.values?.length > 0);
};

const SectionHeader = ({ title, subtitle, themeStyles, theme }) => (
  <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
    <h2 style={{
      fontSize: 'clamp(1.5rem, 3vw, 2rem)',
      fontWeight: '700',
      color: themeStyles?.text || '#111827',
      marginBottom: '0.5rem',
      fontFamily: theme?.fonts?.heading || 'inherit'
    }}>
      {title}
    </h2>
    <div style={{
      width: '40px',
      height: '3px',
      backgroundColor: themeStyles?.primary || '#2563eb',
      borderRadius: '2px',
      margin: '0 auto 0.75rem'
    }} />
    {subtitle && (
      <p style={{
        fontSize: '1rem',
        color: themeStyles?.textMuted || '#6b7280',
        maxWidth: '500px',
        margin: '0 auto'
      }}>
        {subtitle}
      </p>
    )}
  </div>
);

/**
 * Single product card with two layouts: grid (default) and list.
 * Bundled in this file because it's only used by the catalog grid.
 * For featured-row cards, see FeaturedProductsRow.js — those are
 * intentionally separate (different badge / hover / aspect ratio).
 */
const ProductCard = ({ product, store, themeStyles, viewMode, formatPrice, getStorePath, dispatch, transitionCartAction, isUpdating, toggleWishlist, isInWishlist, wishlistLoading }) => {
  const { trackAddToCart } = useGA4();
  const navigate = useNavigate();
  const toast = useToast();
  const [imageFailed, setImageFailed] = useState(false);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (hasProductOptions(product)) {
      navigate(getStorePath(`product/${product.slug || product._id || product.id}`));
      return;
    }

    try {
      await transitionCartAction(async () => {
        await dispatch(addToCart({
          product: {
            _id: product._id || product.id,
            id: product.id || product._id,
            name: product.name,
            price: parseFloat(product.price),
            image: product.primaryImage || (product.gallery && product.gallery[0]),
            storeSlug: store?.slug,
            variations: null
          },
          quantity: 1
        })).unwrap();
      }, 'add');

      trackAddToCart(product, 1);
      toast.success(`${product.name} added to cart`);

      // Abandoned-cart ping: fire-and-forget. The cart_session_id is
      // generated on first add and persists for the session, so a
      // returning anonymous customer keeps the same recovery thread.
      try {
        let sessionId = sessionStorage.getItem('cart_session_id');
        if (!sessionId) {
          sessionId = crypto.randomUUID ? crypto.randomUUID() : String(Date.now());
          sessionStorage.setItem('cart_session_id', sessionId);
        }
        await axios.post('/abandoned-carts/track', {
          sessionId,
          items: [{
            productId: product.id || product._id,
            quantity: 1,
            price: parseFloat(product.price),
            name: product.name,
            image: product.primaryImage || (product.gallery && product.gallery[0]) || ''
          }],
          storeSlug: store?.slug,
          subtotal: parseFloat(product.price),
          currency: 'NGN'
        });
      } catch (err) {
        // non-blocking: a failed abandoned-cart write should never
        // surface to the customer mid-add-to-cart
      }
    } catch (err) {
      toast.error('Could not add item to cart. Please try again.');
    }
  };

  const productId = product.id || product._id;
  const inWishlist = isInWishlist(productId);
  const hasImage = product.primaryImage || (product.gallery && product.gallery.length > 0);
  const imageUrl = product.primaryImage || (product.gallery && product.gallery[0]);
  const availability = getStorefrontVariantAvailability(product);
  const isOutOfStock = !availability.hasStock;
  const isAppointment = product.productType === 'appointment' || product.category === 'appointment';
  const hasOptions = hasProductOptions(product);
  const categoryLabel = product.category || product.productType || '';

  if (viewMode === 'list') {
    return (
      <div style={{
        display: 'flex',
        gap: '1rem',
        backgroundColor: themeStyles?.background || 'white',
        borderRadius: CARD_RADIUS,
        overflow: 'hidden',
        border: `1px solid ${themeStyles?.border || '#e5e7eb'}`,
        boxShadow: CARD_SHADOW,
        transition: 'box-shadow 0.2s ease',
        padding: '0.875rem',
        opacity: isOutOfStock ? 0.5 : 1
      }}>
        <Link
          to={getStorePath(`product/${product.slug || product._id || product.id}`)}
          style={{ textDecoration: 'none', display: 'block', flexShrink: 0 }}
        >
          <div style={{
            width: '120px',
            height: '120px',
            backgroundColor: themeStyles?.backgroundAlt || '#f9fafb',
            borderRadius: '8px',
            overflow: 'hidden',
            position: 'relative'
          }}>
            {hasImage && !imageFailed ? (
              <img src={imageUrl} alt={product.name} onError={() => setImageFailed(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PackageIcon size={36} color={themeStyles?.textMuted || '#d1d5db'} />
              </div>
            )}
            {product.activeSale && !isOutOfStock && (
              <div style={{
                position: 'absolute',
                top: '0.5rem',
                left: '0.5rem',
                backgroundColor: '#111827',
                color: 'white',
                padding: '0.2rem 0.45rem',
                borderRadius: '6px',
                fontSize: '0.625rem',
                fontWeight: '700',
                letterSpacing: '0.03em'
              }}>
                {product.activeSale.badgeText || 'SALE'}
              </div>
            )}
          </div>
        </Link>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
          <div>
            <Link to={getStorePath(`product/${product.slug || product._id || product.id}`)} style={{ textDecoration: 'none' }}>
              {categoryLabel && (
                <div style={{
                  fontSize: '0.75rem',
                  color: themeStyles?.textMuted || '#6b7280',
                  marginBottom: '0.25rem',
                  textTransform: 'capitalize'
                }}>
                  {categoryLabel}
                </div>
              )}
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '650',
                color: themeStyles?.text || '#111827',
                marginBottom: '0.375rem',
                lineHeight: '1.4'
              }}>
                {product.name}
              </h3>
            </Link>
            {product.description && (
              <p style={{
                fontSize: '0.8125rem',
                color: themeStyles?.textMuted || '#6b7280',
                marginBottom: '0.5rem',
                lineHeight: '1.5',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}>
                {product.description}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{
              fontSize: '1.0625rem',
              fontWeight: '700',
              color: themeStyles?.primary || '#2563eb'
            }}>
              {formatPrice(product.price)}
            </span>
            {product.compareAtPrice && Number(product.compareAtPrice) > Number(product.price) && (
              <span style={{
                marginLeft: '0.45rem',
                fontSize: '0.8125rem',
                color: themeStyles?.textMuted || '#6b7280',
                textDecoration: 'line-through'
              }}>
                {formatPrice(product.compareAtPrice)}
              </span>
            )}
            {isOutOfStock ? (
              <span style={{ fontSize: '0.8125rem', color: themeStyles?.textMuted || '#9ca3af', fontWeight: '500' }}>Out of Stock</span>
            ) : (
              <button
                onClick={handleAddToCart}
                disabled={isUpdating}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: themeStyles?.primary || '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '0.8125rem',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
              >
                {isAppointment ? 'Book' : hasOptions ? 'Choose Options' : 'Add to Cart'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Grid view
  return (
    <div style={{
      backgroundColor: themeStyles?.background || 'white',
      borderRadius: '10px',
      overflow: 'hidden',
      border: `1px solid ${themeStyles?.border || '#e5e7eb'}`,
      boxShadow: CARD_SHADOW,
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      display: 'flex',
      flexDirection: 'column',
      opacity: isOutOfStock ? 0.5 : 1
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = CARD_HOVER_SHADOW;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = CARD_SHADOW;
    }}>
      <Link
        to={getStorePath(`product/${product.slug || product._id || product.id}`)}
        style={{ textDecoration: 'none', display: 'block' }}
      >
        <div style={{
          width: '100%',
          aspectRatio: '4/5',
          position: 'relative',
          backgroundColor: themeStyles?.backgroundAlt || '#f9fafb',
          overflow: 'hidden'
        }}>
          {hasImage && !imageFailed ? (
            <img
              src={imageUrl}
              alt={product.name}
              onError={() => setImageFailed(true)}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.3s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            />
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <PackageIcon size={48} color={themeStyles?.textMuted || '#d1d5db'} />
            </div>
          )}

          <button
            onClick={(e) => toggleWishlist(productId, e)}
            disabled={wishlistLoading}
            aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
            style={{
              position: 'absolute',
              top: '0.625rem',
              right: '0.625rem',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              // Dark-mode-aware surface; was hardcoded white which
              // stayed light in dark mode (visible white circles on
              // every product card).
              backgroundColor: themeStyles?.background || '#ffffff',
              border: 'none',
              cursor: wishlistLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
              zIndex: 5,
              transition: 'transform 0.15s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            <HeartIcon filled={inWishlist} size={16} />
          </button>

          {isOutOfStock && (
            <div style={{
              position: 'absolute',
              top: '0.625rem',
              left: '0.625rem',
              padding: '0.25rem 0.5rem',
              backgroundColor: '#ef4444',
              color: 'white',
              borderRadius: '6px',
              fontSize: '0.6875rem',
              fontWeight: '600'
            }}>
              Out of Stock
            </div>
          )}
          {product.activeSale && !isOutOfStock && (
            <div style={{
              position: 'absolute',
              top: '0.625rem',
              left: '0.625rem',
              padding: '0.25rem 0.5rem',
              backgroundColor: '#111827',
              color: 'white',
              borderRadius: '6px',
              fontSize: '0.6875rem',
              fontWeight: '700',
              letterSpacing: '0.03em'
            }}>
              {product.activeSale.badgeText || 'SALE'}
            </div>
          )}
        </div>
      </Link>

      <div style={{ padding: '0.875rem 0.95rem 0.95rem', display: 'flex', flexDirection: 'column', flex: 1 }}>
        {categoryLabel && (
          <div style={{
            width: 'fit-content',
            maxWidth: '100%',
            fontSize: '0.72rem',
            color: themeStyles?.textMuted || '#6b7280',
            marginBottom: '0.4rem',
            textTransform: 'capitalize',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {categoryLabel}
          </div>
        )}
        <Link
          to={getStorePath(`product/${product.slug || product._id || product.id}`)}
          style={{ textDecoration: 'none', display: 'block', marginBottom: '0.5rem' }}
        >
          <h3 style={{
            fontSize: '0.95rem',
            fontWeight: '650',
            color: themeStyles?.text || '#111827',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: '1.4',
            minHeight: '2.625rem'
          }}>
            {product.name}
          </h3>
        </Link>

        <div style={{ marginBottom: '0.75rem' }}>
          <span style={{
            fontSize: '1.0625rem',
            fontWeight: '700',
            color: themeStyles?.primary || '#2563eb'
          }}>
            {formatPrice(product.price)}
          </span>
          {product.compareAtPrice && Number(product.compareAtPrice) > Number(product.price) && (
            <span style={{
              marginLeft: '0.45rem',
              fontSize: '0.8125rem',
              color: themeStyles?.textMuted || '#6b7280',
              textDecoration: 'line-through'
            }}>
              {formatPrice(product.compareAtPrice)}
            </span>
          )}
        </div>

        <div style={{ marginTop: 'auto' }}>
          {isOutOfStock ? (
            <div style={{
              width: '100%',
              padding: '0.5rem',
              textAlign: 'center',
              fontSize: '0.8125rem',
              color: themeStyles?.textMuted || '#9ca3af',
              fontWeight: '500'
            }}>
              Out of Stock
            </div>
          ) : (
            <button
              onClick={handleAddToCart}
              disabled={isUpdating}
              style={{
                width: '100%',
                padding: '0.5rem',
                backgroundColor: themeStyles?.primary || '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '0.8125rem',
                cursor: 'pointer',
                transition: 'opacity 0.15s ease',
                fontFamily: 'inherit'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              {isAppointment ? 'Book' : hasOptions ? 'Choose Options' : 'Add to Cart'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const HomeProductCatalog = ({
  sectionRef,
  selectedCategory,
  filteredProducts,
  paginatedProducts,
  searchQuery,
  onSearchChange,
  categories,
  onCategoryChange,
  sortBy,
  onSortChange,
  viewMode,
  onViewModeChange,
  currentPage,
  totalPages,
  onPageChange,
  themeStyles,
  theme,
  store,
  formatPrice,
  getStorePath,
  dispatch,
  transitionCartAction,
  isUpdating,
  toggleWishlist,
  isInWishlist,
  wishlistLoading,
  sectionTitle,
  sectionSubtitle,
  showControls = true,
}) => {
  const productCount = filteredProducts?.length || 0;
  const fallbackTitle = selectedCategory === 'all' ? 'All Products' : `${selectedCategory} Products`;
  const fallbackSubtitle = selectedCategory === 'all'
    ? `Showing ${productCount} product${productCount !== 1 ? 's' : ''}`
    : `Explore ${productCount} ${selectedCategory.toLowerCase()} product${productCount !== 1 ? 's' : ''}`;
  const headerTitle = sectionTitle || fallbackTitle;
  const headerSubtitle = sectionSubtitle || fallbackSubtitle;

  return (
    <div
      ref={sectionRef}
      className="hp-section-pad"
      style={{
        maxWidth: SECTION_MAX_WIDTH,
        margin: '0 auto',
        padding: `3rem ${SECTION_HPAD}`,
        scrollMarginTop: '80px'
      }}
    >
      <style>{`
        .hp-products-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.25rem;
        }
        .hp-products-grid.list-mode {
          grid-template-columns: 1fr;
          gap: 0.75rem;
        }
        @media (max-width: 1024px) {
          .hp-products-grid { grid-template-columns: repeat(3, 1fr); }
          .hp-featured-grid { grid-template-columns: repeat(3, 1fr) !important; }
        }
        @media (max-width: 768px) {
          .hp-products-grid { grid-template-columns: repeat(2, 1fr); }
          .hp-featured-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 1rem !important; }
          .hp-section-pad { padding: 2rem 1rem !important; }
        }
        @media (max-width: 480px) {
          .hp-products-grid { grid-template-columns: repeat(2, 1fr); gap: 0.75rem; }
          .hp-featured-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 0.75rem !important; }
        }
      `}</style>

      <SectionHeader
        title={headerTitle}
        subtitle={headerSubtitle}
        themeStyles={themeStyles}
        theme={theme}
      />

      {showControls && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.75rem',
          marginBottom: '1.5rem',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => onSearchChange?.(e.target.value)}
                aria-label="Search products"
                style={{
                  padding: '0.5rem 2rem 0.5rem 0.75rem',
                  fontSize: '0.8125rem',
                  border: `1px solid ${themeStyles?.border || '#e5e7eb'}`,
                  borderRadius: '8px',
                  outline: 'none',
                  fontFamily: 'inherit',
                  backgroundColor: themeStyles?.background || 'white',
                  color: themeStyles?.text || '#111827',
                  width: '180px'
                }}
              />
              <div style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                <SearchIcon size={14} color={themeStyles?.textMuted || '#9ca3af'} />
              </div>
            </div>

            <select
              value={selectedCategory}
              onChange={(e) => onCategoryChange?.(e.target.value)}
              aria-label="Filter by category"
              style={{
                padding: '0.5rem 0.75rem',
                fontSize: '0.8125rem',
                border: `1px solid ${themeStyles?.border || '#e5e7eb'}`,
                borderRadius: '8px',
                outline: 'none',
                fontFamily: 'inherit',
                backgroundColor: themeStyles?.background || 'white',
                color: themeStyles?.text || '#111827',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Categories</option>
              {(categories || []).map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            <select
              value={sortBy}
              onChange={(e) => onSortChange?.(e.target.value)}
              aria-label="Sort products"
              style={{
                padding: '0.5rem 0.75rem',
                fontSize: '0.8125rem',
                border: `1px solid ${themeStyles?.border || '#e5e7eb'}`,
                borderRadius: '8px',
                outline: 'none',
                fontFamily: 'inherit',
                backgroundColor: themeStyles?.background || 'white',
                color: themeStyles?.text || '#111827',
                cursor: 'pointer'
              }}
            >
              <option value="default">Sort By</option>
              <option value="a-z">Name: A-Z</option>
              <option value="z-a">Name: Z-A</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: '0.25rem', backgroundColor: themeStyles?.backgroundAlt || '#f3f4f6', borderRadius: '8px', padding: '2px' }}>
            <button
              onClick={() => onViewModeChange?.('grid')}
              aria-label="Grid view"
              aria-pressed={viewMode === 'grid'}
              style={{
                padding: '0.375rem 0.5rem',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: viewMode === 'grid' ? (themeStyles?.background || 'white') : 'transparent',
                boxShadow: viewMode === 'grid' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <GridIcon size={16} color={viewMode === 'grid' ? (themeStyles?.primary || '#2563eb') : (themeStyles?.textMuted || '#9ca3af')} />
            </button>
            <button
              onClick={() => onViewModeChange?.('list')}
              aria-label="List view"
              aria-pressed={viewMode === 'list'}
              style={{
                padding: '0.375rem 0.5rem',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: viewMode === 'list' ? (themeStyles?.background || 'white') : 'transparent',
                boxShadow: viewMode === 'list' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <ListIcon size={16} color={viewMode === 'list' ? (themeStyles?.primary || '#2563eb') : (themeStyles?.textMuted || '#9ca3af')} />
            </button>
          </div>
        </div>
      )}

      {paginatedProducts && paginatedProducts.length > 0 ? (
        <>
          <div className={`hp-products-grid${viewMode === 'list' ? ' list-mode' : ''}`}>
            {paginatedProducts.map((product) => (
              <ProductCard
                key={product._id || product.id}
                product={product}
                store={store}
                themeStyles={themeStyles}
                viewMode={viewMode}
                formatPrice={formatPrice}
                getStorePath={getStorePath}
                dispatch={dispatch}
                transitionCartAction={transitionCartAction}
                isUpdating={isUpdating}
                toggleWishlist={toggleWishlist}
                isInWishlist={isInWishlist}
                wishlistLoading={wishlistLoading}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '0.375rem',
              marginTop: '2.5rem',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => onPageChange?.(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '0.5rem 0.75rem',
                  border: `1px solid ${themeStyles?.border || '#e5e7eb'}`,
                  borderRadius: '8px',
                  backgroundColor: themeStyles?.background || 'white',
                  color: currentPage === 1 ? (themeStyles?.textMuted || '#d1d5db') : (themeStyles?.text || '#111827'),
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  fontSize: '0.8125rem',
                  transition: 'all 0.15s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                <ChevronLeftIcon size={14} />
                Prev
              </button>

              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1;
                if (totalPages <= 7 || page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1) {
                  const isActive = currentPage === page;
                  return (
                    <button
                      key={page}
                      onClick={() => onPageChange?.(page)}
                      aria-current={isActive ? 'page' : undefined}
                      style={{
                        padding: '0.5rem',
                        border: `1px solid ${isActive ? (themeStyles?.primary || '#2563eb') : (themeStyles?.border || '#e5e7eb')}`,
                        borderRadius: '8px',
                        backgroundColor: isActive ? (themeStyles?.primary || '#2563eb') : (themeStyles?.background || 'white'),
                        color: isActive ? 'white' : (themeStyles?.text || '#111827'),
                        cursor: 'pointer',
                        fontWeight: isActive ? '600' : '400',
                        fontSize: '0.8125rem',
                        minWidth: '36px',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {page}
                    </button>
                  );
                }
                if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} style={{ color: themeStyles?.textMuted || '#9ca3af', fontSize: '0.8125rem', padding: '0 0.25rem' }}>...</span>;
                }
                return null;
              })}

              <button
                onClick={() => onPageChange?.(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '0.5rem 0.75rem',
                  border: `1px solid ${themeStyles?.border || '#e5e7eb'}`,
                  borderRadius: '8px',
                  backgroundColor: themeStyles?.background || 'white',
                  color: currentPage === totalPages ? (themeStyles?.textMuted || '#d1d5db') : (themeStyles?.text || '#111827'),
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  fontSize: '0.8125rem',
                  transition: 'all 0.15s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                Next
                <ChevronRightIcon size={14} />
              </button>
            </div>
          )}
        </>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '4rem 1.5rem',
          color: themeStyles?.textMuted || '#6b7280'
        }}>
          <div style={{ marginBottom: '1rem' }}>
            <EmptySearchIcon size={48} color={themeStyles?.textMuted || '#d1d5db'} />
          </div>
          <p style={{ fontSize: '1.0625rem', fontWeight: '500', marginBottom: '0.375rem', color: themeStyles?.text || '#374151' }}>
            No products found
          </p>
          <p style={{ fontSize: '0.875rem' }}>
            Try adjusting your search or filters
          </p>
        </div>
      )}
    </div>
  );
};

export default HomeProductCatalog;
